/**
 * Shared pricing helpers for app plans.
 *
 * Plan shape (extended, backwards-compatible):
 *   {
 *     name, currency, description, features[], popular,
 *     model: "free" | "flat" | "per_unit",
 *     // flat & per_unit:
 *     monthly, yearly,                          // for per_unit these are the BASE price
 *     // per_unit only:
 *     unit: "horse" | "user" | "stall" | "rider" | "ride" | "custom",
 *     unit_custom: string,                       // when unit === "custom"
 *     unit_price_monthly, unit_price_yearly,
 *   }
 *
 * Legacy plans (no `model`) are treated as "flat" (or "free" if both prices = 0).
 */

export const PRICING_UNITS = ["horse", "user", "stall", "rider", "ride", "custom"];

export const UNIT_LABELS = {
  en: { horse: "horse", user: "user", stall: "stall", rider: "rider", ride: "ride", custom: "unit" },
  nl: { horse: "paard", user: "gebruiker", stall: "stal", rider: "ruiter", ride: "rit",  custom: "eenheid" },
};

export const currencySymbol = (c) => {
  if (c === "USD") return "$";
  if (c === "GBP") return "£";
  return "€";
};

/** Infer model for legacy data. */
export const planModel = (p) => {
  if (p?.model) return p.model;
  const m = Number(p?.monthly) || 0;
  const y = Number(p?.yearly) || 0;
  return m === 0 && y === 0 ? "free" : "flat";
};

const unitLabel = (p, lang) => {
  const u = p?.unit || "custom";
  if (u === "custom") return (p?.unit_custom || UNIT_LABELS[lang]?.custom || "unit").trim();
  return UNIT_LABELS[lang]?.[u] || UNIT_LABELS.en[u] || u;
};

/**
 * Returns the headline price line and an optional secondary line.
 * Used on both AppDetailPage and VersusPage so formatting stays consistent.
 *
 * Shape: { primary: string, secondary: string|null, isFree: boolean }
 */
export const formatPlanPrice = (p, lang = "en") => {
  const isNl = lang === "nl";
  const model = planModel(p);
  const sym = currencySymbol(p?.currency);

  if (model === "free") {
    return { primary: isNl ? "Gratis" : "Free", secondary: null, isFree: true };
  }

  if (model === "per_unit") {
    const base = Number(p?.monthly) || 0;
    const unit = Number(p?.unit_price_monthly) || 0;
    const lbl = unitLabel(p, lang);
    const moSuffix = isNl ? "mnd" : "mo";

    let primary;
    if (base > 0 && unit > 0) {
      primary = `${sym}${base} + ${sym}${unit}/${lbl}`;
    } else if (unit > 0) {
      primary = `${sym}${unit}/${lbl}`;
    } else if (base > 0) {
      primary = `${sym}${base}`;
    } else {
      return { primary: isNl ? "Gratis" : "Free", secondary: null, isFree: true };
    }
    primary = `${primary}/${moSuffix}`;
    const yearlyUnit = Number(p?.unit_price_yearly) || 0;
    const yearlyBase = Number(p?.yearly) || 0;
    let secondary = null;
    if (yearlyBase > 0 || yearlyUnit > 0) {
      const yLbl = isNl ? "jaar" : "year";
      if (yearlyBase > 0 && yearlyUnit > 0) {
        secondary = `${sym}${yearlyBase} + ${sym}${yearlyUnit}/${lbl}/${yLbl}`;
      } else if (yearlyUnit > 0) {
        secondary = `${sym}${yearlyUnit}/${lbl}/${yLbl}`;
      } else {
        secondary = `${sym}${yearlyBase}/${yLbl}`;
      }
    }
    return { primary, secondary, isFree: false };
  }

  // flat
  const monthly = Number(p?.monthly) || 0;
  const yearly = Number(p?.yearly) || 0;
  if (monthly === 0 && yearly === 0) {
    return { primary: isNl ? "Gratis" : "Free", secondary: null, isFree: true };
  }
  const moSuffix = isNl ? "mnd" : "mo";
  const yLbl = isNl ? "jaar" : "year";
  if (monthly > 0) {
    return {
      primary: `${sym}${monthly}/${moSuffix}`,
      secondary: yearly > 0 ? `${sym}${yearly}/${yLbl}` : null,
      isFree: false,
    };
  }
  return { primary: `${sym}${yearly}/${yLbl}`, secondary: null, isFree: false };
};

/** Used by comparison page to pick the lowest paid plan. */
export const planEffectiveMonthly = (p) => {
  const model = planModel(p);
  if (model === "free") return 0;
  if (model === "per_unit") {
    // Use base + 1 unit as a comparable lower-bound
    return (Number(p?.monthly) || 0) + (Number(p?.unit_price_monthly) || 0);
  }
  return Number(p?.monthly) || 0;
};
